import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  AiOutlineTeam,
  AiOutlineLock,
  AiOutlineSearch,
  AiOutlinePlus,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineUser,
  AiOutlineWarning,
} from "react-icons/ai";
import api from "../../services/api";

// Admin do schedule vem exclusivamente da coluna is_admin (USUARIOS_MONITOR_NOGLE).
const ROLES_MAP = {
  1: { label: "Admin", cor: "#02EA75", desc: "Acesso total de administrador" },
  0: { label: "Usuario", cor: "#3182CE", desc: "Acesso padrao ao calendario" },
};

const FORM_INICIAL = { id: null, name: "", email: "", admissionDate: "", password: "", isAdmin: 0, isComercial: 0 };

const getUserIsAdmin = (user) =>
  Number(
    user?.isAdmin ??
      user?.IS_ADMIN ??
      user?.is_admin ??
      user?.is_admin_USUARIOS_MONITOR_NOGLE ??
      user?.IS_ADMIN_USUARIOS_MONITOR_NOGLE ??
      0
  ) === 1;

const getInitials = (nome) => {
  if (!nome) return "?";
  return nome.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
};

export default function UsuariosPage() {
  const toast = useToast();

  const [me, setMe] = useState(null);
  const [ready, setReady] = useState(false);
  const isAdmin = getUserIsAdmin(me);

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");

  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [usuarioVisualizando, setUsuarioVisualizando] = useState(null);

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const [form, setForm] = useState(FORM_INICIAL);
  const [editando, setEditando] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erpExists, setErpExists] = useState(false);

  const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();
  const [usuarioExcluindo, setUsuarioExcluindo] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Descobre o usuário logado (para gate de admin e bloquear excluir a si mesmo).
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/calendar/me");
        setMe(res.data || null);
      } catch {
        setMe(null);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/calendar/employees");
      setUsuarios(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erro ao carregar usuários:", err);
      toast({ title: "Erro ao carregar usuários", status: "error", duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (ready && isAdmin) fetchUsuarios();
  }, [ready, isAdmin, fetchUsuarios]);

  // Aviso: verifica se o e-mail já existe no ERP (debounce).
  useEffect(() => {
    const email = form.email?.trim();
    if (!isFormOpen || !email || !email.includes("@")) {
      setErpExists(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/calendar/usuarios/erp-check?email=${encodeURIComponent(email)}`);
        setErpExists(!!res.data?.existsInErp);
      } catch {
        setErpExists(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.email, isFormOpen]);

  const filtrados = useMemo(() => {
    if (!busca.trim()) return usuarios;
    const q = busca.toLowerCase();
    return usuarios.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
    );
  }, [usuarios, busca]);

  const handleVisualizar = (user) => {
    setUsuarioVisualizando(user);
    onViewOpen();
  };

  const openCreate = () => {
    setEditando(false);
    setForm(FORM_INICIAL);
    setErpExists(false);
    onFormOpen();
  };

  const openEdit = (user) => {
    setEditando(true);
    setForm({
      id: user.id,
      name: user.name || "",
      email: user.email || "",
      admissionDate: user.admissionDate || "",
      password: "",
      isAdmin: getUserIsAdmin(user) ? 1 : 0,
      isComercial: Number(user.isComercial) === 1 ? 1 : 0,
    });
    setErpExists(false);
    onFormOpen();
  };

  const handleSalvar = async () => {
    if (!form.email.trim()) {
      toast({ title: "O e-mail é obrigatório", status: "warning", duration: 2000 });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim() || null,
        admissionDate: form.admissionDate || null,
        password: form.password.trim() || null,
        isAdmin: Number(form.isAdmin) === 1 ? 1 : 0,
        isComercial: Number(form.isComercial) === 1 ? 1 : 0,
      };
      if (editando) {
        await api.put(`/calendar/usuarios/${form.id}`, payload);
        toast({ title: "Usuário atualizado", status: "success", duration: 2000 });
      } else {
        await api.post("/calendar/usuarios", { ...payload, email: form.email.trim() });
        toast({ title: "Usuário criado", status: "success", duration: 2000 });
      }
      await fetchUsuarios();
      onFormClose();
    } catch (err) {
      console.error("Erro ao salvar usuário:", err);
      toast({
        title: "Erro ao salvar",
        description: err?.response?.data?.message || "Falha ao salvar usuário.",
        status: "error",
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (user) => {
    setUsuarioExcluindo(user);
    onDelOpen();
  };

  const handleConfirmarExcluir = async () => {
    if (!usuarioExcluindo) return;
    setDeleting(true);
    try {
      await api.delete(`/calendar/usuarios/${usuarioExcluindo.id}`);
      toast({ title: "Usuário removido do schedule", status: "info", duration: 2000 });
      await fetchUsuarios();
      onDelClose();
      setUsuarioExcluindo(null);
    } catch (err) {
      console.error("Erro ao excluir usuário:", err);
      toast({
        title: "Erro ao excluir",
        description: err?.response?.data?.message || "Falha ao excluir usuário.",
        status: "error",
        duration: 4000,
      });
    } finally {
      setDeleting(false);
    }
  };

  // Gate: tela só para admins.
  if (!ready) {
    return (
      <Flex justify="center" py={20}>
        <Spinner size="lg" color="secondary" />
      </Flex>
    );
  }

  if (!isAdmin) {
    return (
      <VStack spacing={3} py={20} align="center" color="textSecondary">
        <Icon as={AiOutlineLock} boxSize="32px" color="textMuted" />
        <Text fontWeight="800" color="textPrimary">Acesso restrito</Text>
        <Text fontSize="sm" textAlign="center" maxW="420px">
          Esta tela é exclusiva para administradores.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={4}
        w="100%"
      >
        <Heading size="lg" color="textPrimary" fontWeight="900">
          <HStack spacing={3}>
            <Icon as={AiOutlineTeam} color="secondary" />
            <Text>Usuários</Text>
          </HStack>
        </Heading>
        {/* Busca e botão dividem a mesma linha em qualquer largura */}
        <HStack spacing={3} flexWrap="nowrap">
          <InputGroup flex="1" maxW={{ md: "260px" }} minW="0" size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={AiOutlineSearch} color="textSecondary" />
            </InputLeftElement>
            <Input
              placeholder="Buscar usuário..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              autoComplete="off"
              bg="surface"
              border="1px solid"
              borderColor="borderPrimary"
              _focus={{ borderColor: "secondary", boxShadow: "none" }}
              color="textPrimary"
              borderRadius="xl"
            />
          </InputGroup>
          <Button
            leftIcon={<AiOutlinePlus />}
            size="sm"
            colorScheme="green"
            fontWeight="800"
            flexShrink={0}
            onClick={openCreate}
          >
            Novo Usuário
          </Button>
        </HStack>
      </Flex>

      {/* Tabela */}
      <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
        <Flex align="center" gap={3} p={4} bg="surfaceRaised" borderBottom="1px solid" borderColor="borderPrimary">
          <Icon as={AiOutlineLock} color="secondary" boxSize="20px" />
          <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1.5px" fontWeight="800">
            Gerenciamento de Acesso
          </Heading>
          {loading ? (
            <Spinner size="xs" color="secondary" ml="auto" />
          ) : (
            <Badge ml="auto" bg="rgba(2,234,117,0.15)" color="secondary" fontWeight="800">
              {filtrados.length}
            </Badge>
          )}
        </Flex>

        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Nome (Colaborador)</Th>
                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">E-mail</Th>
                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Permissão</Th>
                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" isNumeric>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={4} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                    <Spinner size="md" color="secondary" mr={2} /> Carregando usuários...
                  </Td>
                </Tr>
              ) : filtrados.length === 0 ? (
                <Tr>
                  <Td colSpan={4} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                    Nenhum usuário encontrado
                  </Td>
                </Tr>
              ) : (
                filtrados.map((user) => {
                  const roleInfo = ROLES_MAP[getUserIsAdmin(user) ? 1 : 0];
                  const ativo = Number(user.active) >= 1;
                  const isSelf = String(user.id) === String(me?.ID);
                  return (
                    <Tr
                      key={user.id}
                      _hover={{ bg: "surfaceHover" }}
                      cursor="pointer"
                      onClick={() => handleVisualizar(user)}
                    >
                      <Td borderColor="borderPrimary">
                        <HStack spacing={3}>
                          <Avatar
                            size="sm"
                            name={user.name || user.email}
                            bg={`${roleInfo.cor}25`}
                            color={roleInfo.cor}
                            fontWeight="900"
                            fontSize="xs"
                          >
                            {getInitials(user.name || user.email)}
                          </Avatar>
                          <Text fontWeight="800" fontSize="sm" color="textPrimary">
                            {user.name || "—"}
                          </Text>
                        </HStack>
                      </Td>
                      <Td borderColor="borderPrimary">
                        <Text fontSize="sm" color="textSecondary">{user.email}</Text>
                      </Td>
                      <Td borderColor="borderPrimary">
                        <HStack spacing={2}>
                          <Badge bg={`${roleInfo.cor}20`} color={roleInfo.cor} fontSize="xs" fontWeight="700" borderRadius="md" px={2}>
                            {roleInfo.label}
                          </Badge>
                          {Number(user.isComercial) === 1 && (
                            <Badge bg="rgba(236,201,75,0.15)" color="#ECC94B" fontSize="xs" fontWeight="700" borderRadius="md" px={2}>
                              Comercial
                            </Badge>
                          )}
                          <Badge
                            bg={ativo ? "rgba(2,234,117,0.15)" : "rgba(229,62,62,0.12)"}
                            color={ativo ? "#02EA75" : "#FC8181"}
                            fontSize="10px"
                            fontWeight="700"
                            borderRadius="md"
                            px={2}
                          >
                            {ativo ? "Ativo" : "Inativo"}
                          </Badge>
                        </HStack>
                      </Td>
                      <Td borderColor="borderPrimary" isNumeric onClick={(e) => e.stopPropagation()}>
                        <HStack spacing={1} justify="flex-end">
                          <IconButton
                            aria-label="Editar"
                            icon={<AiOutlineEdit />}
                            size="xs"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ color: "secondary", bg: "surfaceHover" }}
                            onClick={() => openEdit(user)}
                          />
                          <IconButton
                            aria-label="Excluir"
                            icon={<AiOutlineDelete />}
                            size="xs"
                            variant="ghost"
                            color="textSecondary"
                            isDisabled={isSelf}
                            title={isSelf ? "Você não pode excluir o próprio usuário" : "Excluir"}
                            _hover={{ color: "red.400", bg: "rgba(229,62,62,0.1)" }}
                            onClick={() => openDelete(user)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Modal Visualizar */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} isCentered size="lg">
        <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.600" />
        <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
          <ModalCloseButton color="textSecondary" />
          <ModalBody pt={8} pb={4} px={6}>
            {usuarioVisualizando && (() => {
              const roleInfo = ROLES_MAP[getUserIsAdmin(usuarioVisualizando) ? 1 : 0];
              const ativo = Number(usuarioVisualizando.active) >= 1;
              return (
                <VStack spacing={5} align="stretch">
                  <Flex direction="column" align="center" gap={3}>
                    <Avatar
                      size="xl"
                      name={usuarioVisualizando.name || usuarioVisualizando.email}
                      bg={`${roleInfo.cor}25`}
                      color={roleInfo.cor}
                      fontWeight="900"
                      fontSize="2xl"
                    >
                      {getInitials(usuarioVisualizando.name || usuarioVisualizando.email)}
                    </Avatar>
                    <VStack spacing={1}>
                      <Text fontSize="xl" fontWeight="900" color="textPrimary" textAlign="center">
                        {usuarioVisualizando.name || "—"}
                      </Text>
                      <HStack spacing={2}>
                        <Badge bg={`${roleInfo.cor}20`} color={roleInfo.cor} fontSize="xs" fontWeight="700" borderRadius="md" px={2}>
                          {roleInfo.label}
                        </Badge>
                        {Number(usuarioVisualizando.isComercial) === 1 && (
                          <Badge bg="rgba(236,201,75,0.15)" color="#ECC94B" fontSize="xs" fontWeight="700" borderRadius="md" px={2}>
                            Comercial
                          </Badge>
                        )}
                        <Badge
                          bg={ativo ? "rgba(2,234,117,0.15)" : "rgba(229,62,62,0.12)"}
                          color={ativo ? "#02EA75" : "#FC8181"}
                          fontSize="10px"
                          fontWeight="700"
                          borderRadius="md"
                          px={2}
                        >
                          {ativo ? "Ativo" : "Inativo"}
                        </Badge>
                      </HStack>
                    </VStack>
                  </Flex>

                  <Divider borderColor="borderPrimary" />

                  <SimpleGrid columns={1} spacing={3}>
                    <Box bg="surfaceRaised" p={3} borderRadius="lg" border="1px solid" borderColor="borderPrimary">
                      <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px" mb={1}>
                        E-mail
                      </Text>
                      <Text fontSize="sm" color="textPrimary" fontWeight="700">
                        {usuarioVisualizando.email || "—"}
                      </Text>
                    </Box>
                    <Box bg="surfaceRaised" p={3} borderRadius="lg" border="1px solid" borderColor="borderPrimary">
                      <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px" mb={1}>
                        Descrição de privilégios
                      </Text>
                      <Text fontSize="sm" color="textPrimary" fontWeight="700">
                        {roleInfo.desc}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </VStack>
              );
            })()}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
            <Button variant="ghost" color="textSecondary" _hover={{ bg: "surfaceHover" }} onClick={onViewClose} size="sm">
              Fechar
            </Button>
            <Button
              leftIcon={<AiOutlineEdit />}
              colorScheme="green"
              fontWeight="800"
              size="sm"
              onClick={() => {
                onViewClose();
                openEdit(usuarioVisualizando);
              }}
            >
              Editar Usuário
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal Criar/Editar */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} isCentered size="lg">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
          <ModalHeader borderBottom="1px solid" borderColor="borderPrimary">
            <HStack spacing={3}>
              <Icon as={AiOutlineUser} color="secondary" boxSize="20px" />
              <Text>{editando ? "Editar Usuário" : "Novo Usuário"}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="textSecondary" />
          <ModalBody py={5}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired={!editando} isDisabled={editando}>
                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                  E-mail
                </FormLabel>
                <Input
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="email@exemplo.com"
                  type="email"
                  autoComplete="off"
                  bg="surfaceRaised"
                  borderColor="borderPrimary"
                  color="textPrimary"
                  _placeholder={{ color: "textMuted" }}
                  _focus={{ borderColor: "secondary", boxShadow: "none" }}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                  Nome
                </FormLabel>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Nome completo"
                  bg="surfaceRaised"
                  borderColor="borderPrimary"
                  color="textPrimary"
                  _placeholder={{ color: "textMuted" }}
                  _focus={{ borderColor: "secondary", boxShadow: "none" }}
                />
              </FormControl>

              <HStack spacing={3} align="flex-start">
                <FormControl>
                  <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                    Permissão
                  </FormLabel>
                  <Select
                    value={form.isAdmin}
                    onChange={(e) => setForm((p) => ({ ...p, isAdmin: Number(e.target.value) }))}
                    bg="surfaceRaised"
                    borderColor="borderPrimary"
                    color="textPrimary"
                    _focus={{ borderColor: "secondary", boxShadow: "none" }}
                    sx={{ "& > option": { background: "var(--chakra-colors-surface)", color: "var(--chakra-colors-textPrimary)" } }}
                  >
                    <option value={0}>Usuario</option>
                    <option value={1}>Admin</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                    Time Comercial
                  </FormLabel>
                  <Select
                    value={form.isComercial}
                    onChange={(e) => setForm((p) => ({ ...p, isComercial: Number(e.target.value) }))}
                    bg="surfaceRaised"
                    borderColor="borderPrimary"
                    color="textPrimary"
                    _focus={{ borderColor: "secondary", boxShadow: "none" }}
                    sx={{ "& > option": { background: "var(--chakra-colors-surface)", color: "var(--chakra-colors-textPrimary)" } }}
                  >
                    <option value={0}>Não</option>
                    <option value={1}>Sim</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                    Data de admissão
                  </FormLabel>
                  <Input
                    type="date"
                    value={form.admissionDate || ""}
                    onChange={(e) => setForm((p) => ({ ...p, admissionDate: e.target.value }))}
                    bg="surfaceRaised"
                    borderColor="borderPrimary"
                    color="textPrimary"
                    _focus={{ borderColor: "secondary", boxShadow: "none" }}
                    sx={{
                      "&::-webkit-calendar-picker-indicator": {
                        filter: "invert(52%) sepia(90%) saturate(2000%) hue-rotate(110deg) brightness(100%) contrast(105%)",
                        cursor: "pointer",
                      },
                    }}
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                  Senha {editando && "(deixe em branco para manter)"}
                </FormLabel>
                <Input
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder={editando ? "Manter senha atual" : "Defina a senha de acesso"}
                  type="password"
                  autoComplete="new-password"
                  bg="surfaceRaised"
                  borderColor="borderPrimary"
                  color="textPrimary"
                  _placeholder={{ color: "textMuted" }}
                  _focus={{ borderColor: "secondary", boxShadow: "none" }}
                />
                {erpExists && (
                  <HStack mt={2} spacing={2} align="flex-start" color="#ECC94B">
                    <Icon as={AiOutlineWarning} mt="2px" />
                    <Text fontSize="xs">
                      Este e-mail já tem conta no ERP. Se definir uma senha aqui, ela valerá apenas
                      para o acesso do Schedule (a senha do ERP não é alterada).
                    </Text>
                  </HStack>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
            <Button variant="ghost" color="textSecondary" _hover={{ bg: "surfaceHover" }} onClick={onFormClose} size="sm" isDisabled={saving}>
              Cancelar
            </Button>
            <Button colorScheme="green" fontWeight="800" onClick={handleSalvar} size="sm" isLoading={saving}>
              {editando ? "Salvar" : "Criar Usuário"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal Confirmar Exclusão */}
      <Modal isOpen={isDelOpen} onClose={onDelClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
          <ModalHeader borderBottom="1px solid" borderColor="borderPrimary">
            <HStack spacing={3}>
              <Icon as={AiOutlineWarning} color="#FC8181" boxSize="20px" />
              <Text>Excluir usuário</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="textSecondary" />
          <ModalBody py={5}>
            <Text fontSize="sm" color="textSecondary">
              Remover <strong>{usuarioExcluindo?.name || usuarioExcluindo?.email}</strong> do Schedule?
              O acesso ao calendário e as férias/folgas dele serão removidos. O login do sistema
              comercial não é afetado.
            </Text>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
            <Button variant="ghost" color="textSecondary" _hover={{ bg: "surfaceHover" }} onClick={onDelClose} size="sm" isDisabled={deleting}>
              Cancelar
            </Button>
            <Button colorScheme="red" fontWeight="800" onClick={handleConfirmarExcluir} size="sm" isLoading={deleting}>
              Excluir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
